/**
 * Trumplights product page behaviour.
 * - countdown (flash bar)
 * - product gallery slider + thumbnails
 * - bundle widget: tier selection, per-item variant selects, add-on checkboxes, live pricing
 * - add to cart: posts main variants + add-ons to /cart/add.js and notifies the theme's
 *   cart drawer through the same CartLinesUpdateEvent the theme's product form dispatches
 * - sticky add-to-cart bar
 *
 * Loaded as a module so it can use the theme's importmap (@shopify/events).
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const money = (cents, fmt) => {
  const value = (cents / 100).toFixed(2);
  const [int, dec] = value.split('.');
  const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const amount = `${withCommas}.${dec}`;
  if (fmt && fmt.includes('{{')) {
    return fmt
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', withCommas)
      .replace('{{amount_with_comma_separator}}', `${int},${dec}`)
      .replace('{{amount_no_decimals_with_comma_separator}}', withCommas);
  }
  return `$${amount}`;
};

/* ------------------------------------------------------------------ countdown */
function initCountdown(root) {
  const end = Date.parse(root.dataset.endsAt || '');
  const units = {
    days: root.querySelector('[data-unit="days"]'),
    hours: root.querySelector('[data-unit="hours"]'),
    mins: root.querySelector('[data-unit="mins"]'),
    secs: root.querySelector('[data-unit="secs"]'),
  };
  if (!end || Number.isNaN(end)) return;
  let timer;
  const tick = () => {
    let diff = Math.max(0, end - Date.now());
    if (diff === 0) {
      root.hidden = true;
      clearInterval(timer);
      return;
    }
    const d = Math.floor(diff / 86400000); diff -= d * 86400000;
    const h = Math.floor(diff / 3600000); diff -= h * 3600000;
    const m = Math.floor(diff / 60000); diff -= m * 60000;
    const s = Math.floor(diff / 1000);
    const pad = (n) => String(n).padStart(2, '0');
    if (units.days) units.days.textContent = pad(d);
    if (units.hours) units.hours.textContent = pad(h);
    if (units.mins) units.mins.textContent = pad(m);
    if (units.secs) units.secs.textContent = pad(s);
  };
  tick();
  timer = setInterval(tick, 1000);
  root.querySelector('[data-close]')?.addEventListener('click', () => { root.hidden = true; });
}

/* ------------------------------------------------------------------ gallery */
function initGallery(root) {
  const track = root.querySelector('[data-track]');
  const slides = [...root.querySelectorAll('[data-slide]')];
  const thumbs = [...root.querySelectorAll('[data-thumb]')];
  const dots = [...root.querySelectorAll('[data-dot]')];
  let index = 0;
  const go = (i) => {
    if (!slides.length) return;
    index = (i + slides.length) % slides.length;
    track.style.transform = `translateX(-${index * 100}%)`;
    thumbs.forEach((t, n) => t.setAttribute('aria-current', n === index ? 'true' : 'false'));
    dots.forEach((t, n) => t.setAttribute('aria-current', n === index ? 'true' : 'false'));
    const list = root.querySelector('[data-thumb-list]');
    const active = thumbs[index];
    if (list && active) {
      const left = active.offsetLeft - list.offsetLeft;
      if (left < list.scrollLeft || left + active.offsetWidth > list.scrollLeft + list.clientWidth) {
        list.scrollTo({ left: left - 10, behavior: 'smooth' });
      }
    }
  };
  root.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => go(index - 1)));
  root.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => go(index + 1)));
  thumbs.forEach((t, n) => t.addEventListener('click', () => go(n)));
  dots.forEach((t, n) => t.addEventListener('click', () => go(n)));
  let startX = null;
  track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
  track.addEventListener('touchend', (e) => {
    if (startX === null) return;
    const dx = e.changedTouches[0].clientX - startX;
    if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
    startX = null;
  });
  return { go, get index() { return index; } };
}

/* ------------------------------------------------------------------ bundle + cart */
function initProduct(section) {
  const data = JSON.parse(section.querySelector('[data-tl-product-json]').textContent);
  const fmt = section.dataset.moneyFormat;
  const variants = data.variants; // [{id, title, price, compare_at_price, available, featured_media_id}]
  const tiers = [...section.querySelectorAll('[data-tier]')];
  const atc = section.querySelector('[data-atc]');
  const errorBox = section.querySelector('[data-atc-error]');
  const gallery = section.querySelector('[data-gallery]');
  const galleryApi = gallery ? initGallery(gallery) : null;
  const stickySelect = document.querySelector('[data-sticky-select]');
  const stickyPrice = document.querySelector('[data-sticky-price]');

  const selectedTier = () => tiers.find((t) => t.querySelector('input[type="radio"]').checked) || tiers[0];
  const variantById = (id) => variants.find((v) => String(v.id) === String(id));

  const renderStickyPrice = () => {
    if (!stickyPrice || !stickySelect) return;
    const v = variantById(stickySelect.value);
    if (!v) return;
    stickyPrice.replaceChildren();
    if (v.compare_at_price && v.compare_at_price > v.price) {
      const s = document.createElement('s');
      s.textContent = money(v.compare_at_price, fmt);
      stickyPrice.append(s);
    }
    const b = document.createElement('b');
    b.textContent = money(v.price, fmt);
    stickyPrice.append(b);
  };

  const recalc = () => {
    const tier = selectedTier();
    tiers.forEach((t) => t.classList.toggle('tl-tier--selected', t === tier));
    tiers.forEach((t) => {
      const pct = Number(t.dataset.discount || 0);
      const qty = Number(t.dataset.qty || 1);
      const selects = [...t.querySelectorAll('select[data-item]')];
      let total = 0;
      let compare = 0;
      selects.forEach((s) => {
        const v = variantById(s.value);
        if (!v) return;
        total += v.price;
        compare += v.compare_at_price || v.price;
      });
      if (selects.length === 0) {
        const v = variants[0];
        total = v.price * qty;
        compare = (v.compare_at_price || v.price) * qty;
      }
      const discounted = Math.round(total * (100 - pct) / 100);
      const priceEl = t.querySelector('[data-tier-price]');
      const compareEl = t.querySelector('[data-tier-compare]');
      if (priceEl) priceEl.textContent = money(discounted, fmt);
      if (compareEl) {
        const cmp = Math.max(compare, total);
        compareEl.textContent = cmp > discounted ? money(cmp, fmt) : '';
      }
    });
    renderStickyPrice();
  };

  tiers.forEach((t) => {
    const radio = t.querySelector('input[type="radio"]');
    t.querySelector('[data-tier-head]')?.addEventListener('click', () => { radio.checked = true; recalc(); });
    radio.addEventListener('change', recalc);
    t.querySelectorAll('select[data-item]').forEach((s) => s.addEventListener('change', () => {
      recalc();
      const v = variantById(s.value);
      if (v && v.featured_media_id && galleryApi) {
        const slide = gallery.querySelector(`[data-slide][data-media-id="${v.featured_media_id}"]`);
        if (slide) galleryApi.go(Number(slide.dataset.index));
      }
    }));
    t.querySelectorAll('[data-addon]').forEach((row) => {
      const cb = row.querySelector('input[type="checkbox"]');
      row.addEventListener('click', (e) => {
        if (!t.classList.contains('tl-tier--selected')) { radio.checked = true; recalc(); return; }
        if (e.target.tagName === 'A') return;
        e.preventDefault();
        cb.checked = !cb.checked;
        row.classList.toggle('tl-addon--on', cb.checked);
      });
    });
  });

  const firstSelect = tiers[0]?.querySelector('select[data-item]');
  if (stickySelect && firstSelect) {
    stickySelect.addEventListener('change', () => { firstSelect.value = stickySelect.value; firstSelect.dispatchEvent(new Event('change')); });
    firstSelect.addEventListener('change', () => { stickySelect.value = firstSelect.value; renderStickyPrice(); });
  }

  recalc();

  const showError = (msg) => { if (errorBox) { errorBox.textContent = msg; errorBox.style.display = 'block'; } };
  const clearError = () => { if (errorBox) errorBox.style.display = 'none'; };

  const buildItems = (tier) => {
    const counts = new Map();
    const selects = [...tier.querySelectorAll('select[data-item]')];
    if (selects.length) {
      selects.forEach((s) => counts.set(String(s.value), (counts.get(String(s.value)) || 0) + 1));
    } else {
      counts.set(String(variants[0].id), Number(tier.dataset.qty || 1));
    }
    const items = [...counts.entries()].map(([id, quantity]) => ({ id: Number(id), quantity }));
    tier.querySelectorAll('[data-addon]').forEach((row) => {
      const cb = row.querySelector('input[type="checkbox"]');
      if (cb.checked) items.push({ id: Number(row.dataset.addon), quantity: 1 });
    });
    return items;
  };

  const addToCart = async (button, items) => {
    clearError();
    const unavailable = items.find((i) => { const v = variantById(i.id); return v && v.available === false; });
    if (unavailable) { showError(section.dataset.soldOutText || 'This option is sold out'); return; }
    button.classList.add('tl-atc--loading');
    button.disabled = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    const target = document.querySelector('theme-drawer#cart-drawer') || document;
    target.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    }));

    try {
      const res = await fetch((window.Theme?.routes?.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const json = await res.json();
      if (!res.ok || json.status) {
        const message = json.description || json.message || 'Add to cart failed';
        showError(message);
        deferred.resolve({ cart: null, detail: { didError: true, source: 'tl-bundle' } });
        target.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        return;
      }
      const cartRes = await fetch((window.Theme?.routes?.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: json.items,
          source: 'tl-bundle',
          sourceId: section.id,
          itemCount: items.reduce((n, i) => n + i.quantity, 0),
          productId: data.product_id,
          sections: json.sections,
          didError: false,
        },
      });
      // This store's cart drawer has no auto-open attribute, so open it once the
      // cart-items component has had a moment to morph in the new lines.
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer) {
        // wait for the cart-items view transition to finish so the drawer's own
        // transition is not aborted by it
        const settled = () => (document.activeViewTransition
          ? document.activeViewTransition.finished.catch(() => {}).then(settled)
          : Promise.resolve());
        setTimeout(() => settled().then(() => { if (!drawer.isOpen) drawer.open(); }), 150);
      } else {
        window.location.href = window.Theme?.routes?.cart_url || '/cart';
      }
    } catch (err) {
      showError('Something went wrong. Please try again.');
      deferred.reject(err);
    } finally {
      button.classList.remove('tl-atc--loading');
      button.disabled = false;
    }
  };

  atc?.addEventListener('click', (e) => { e.preventDefault(); addToCart(atc, buildItems(selectedTier())); });
  const stickyBtn = document.querySelector('[data-sticky-atc]');
  stickyBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    // the sticky bar adds one unit of the variant chosen in its own dropdown
    const id = Number(stickySelect?.value || variants[0].id);
    addToCart(stickyBtn, [{ id, quantity: 1 }]);
  });

  // sticky bar visibility: shown once the main add-to-cart has scrolled above the viewport.
  // Horizon scrolls the desktop page inside .page-wrapper, so listen to scroll in the
  // capture phase to catch any scroll container, not only the window.
  const sticky = document.querySelector('[data-sticky-bar]');
  if (sticky && atc) {
    let ticking = false;
    const update = () => {
      ticking = false;
      const visible = atc.getBoundingClientRect().bottom < 0;
      sticky.classList.toggle('tl-sticky--visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }
}

/* ------------------------------------------------------------------ review quotes */
function initQuotes(root) {
  const quotes = [...root.querySelectorAll('[data-quote]')];
  if (quotes.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  let i = 0;
  setInterval(() => {
    quotes[i].classList.remove('tl-quote--active');
    i = (i + 1) % quotes.length;
    quotes[i].classList.add('tl-quote--active');
  }, 5000);
}

/* ------------------------------------------------------------------ boot */
function boot(scope = document) {
  scope.querySelectorAll('[data-tl-countdown]').forEach((el) => { if (!el.dataset.ready) { el.dataset.ready = '1'; initCountdown(el); } });
  scope.querySelectorAll('[data-tl-product]').forEach((el) => { if (!el.dataset.ready) { el.dataset.ready = '1'; initProduct(el); } });
  scope.querySelectorAll('[data-quotes]').forEach((el) => { if (!el.dataset.ready) { el.dataset.ready = '1'; initQuotes(el); } });
}
boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
